'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import Head from 'next/head'
import Image from 'next/image'
import {
  ThreatDataResult,
  ThreatDetailClientProps,
  DetectionDetail,
} from './ThreatDetail.interface'
import LandingPageContainer from '@/src/components/commons/generics/LandingPageContainer/LandingPageContainer'
import styles from './ThreatDetailPage.module.scss'
import Footer from '@/src/components/commons/Footer/Footer'
import TileSwiper from '@/src/components/commons/generics/TileSwiper/TileSwiper' // <-- Added TileSwiper
import { MitreAttackTile } from '@/src/app/threat-library/[threatId]/MitreAttackTile.interface'

import ShortBackArrowWhite from '@/public/assets/images/icon-short-back-arrow-white.svg'
import WarningRedTriangle from '@/public/assets/images/icon_triangle_warning-red.svg'

// --- Constants ---
const API_BASE_URL = 'https://threatlibrary.zscaler.com/api/threats/'

const engineRename: Record<string, string> = {
  URLDB: 'Reputation',
  AV: 'Malware',
  SPM: 'IPS (web)',
  FIREWALL: 'IPS (non-web)',
  InlineYara: 'Inline Yara',
  HASH: 'File Reputation',
  YARA: 'Sandbox',
  WAF: 'WAF',
}

// --- Utility function to format the Mitre Att&ck Matrix for display ---
const formatMitreData = (mitre_tags: ThreatDataResult['mitre_tags']): MitreAttackTile[] => {
  if (!mitre_tags || !mitre_tags['v1.x']) return []

  const formattedData: MitreAttackTile[] = Object.entries(mitre_tags['v1.x']).map(
    ([tactic, attacks]) => ({
      tactic,
      attacks: attacks.map((a) => a.item_name),
    }),
  )

  // --- Sorting Logic for alphabetical order base on 'tactic'
  formattedData.sort((a, b) => {
    return a.tactic.localeCompare(b.tactic)
  })

  return formattedData
}

/**
 * Utility function to correctly parse and map the pipe-delimited 'detection' string(s)
 * into a structured array for the Detection Details table.
 *
 * Handles comma-separated records within an array of detection strings.
 */
const parseDetectionData = (detection: ThreatDataResult['detection']): DetectionDetail[] => {
  if (!detection) return []

  const detectionStrings: string[] = Array.isArray(detection) ? detection : [detection]
  const allRecords: DetectionDetail[] = []

  detectionStrings.forEach((batchStr) => {
    if (!batchStr) return

    // Split the batch string by comma to get individual records
    const records = batchStr.split(',')

    records.forEach((recordStr) => {
      // Clean up whitespace and ensure non-empty string
      const trimmedRecord = recordStr.trim()
      if (!trimmedRecord) return

      // Split the individual record by the pipe-delimiter '||'
      const parts = trimmedRecord.split('||')

      if (parts.length < 6) {
        console.warn(
          `Skipping malformed detection record: ${trimmedRecord}. Expected 6+ parts, got ${parts.length}.`,
        )
        return
      }

      const newRecord: DetectionDetail = {
        connection: parts[0] || '',
        category: parts[1] || '',
        type: parts[2] || '',
        detectionId: parts[3] || '',
        severity: parts[4] || '',
        connectionPort: parts[5] || '',
      }

      allRecords.push(newRecord)
    })
  })

  return allRecords
}

/**
 * Client Component responsible for fetching data and rendering the threat detail page UI.
 */
export default function ThreatDetailClient({ initialThreatId }: ThreatDetailClientProps) {
  const threatId = initialThreatId

  // Hook for accessing URL query parameters for back to search page
  const router = useRouter()

  const [threatData, setThreatData] = useState<ThreatDataResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const handleReturnToSearch = () => {
    router.back()
  }

  // --- Data Fetching Logic (Client-side) ---
  useEffect(() => {
    if (!threatId) {
      setLoading(false)
      setError('Missing threat identifier.')
      return
    }

    const fetchData = async () => {
      setLoading(true)
      setError(null)
      try {
        const apiUrl = API_BASE_URL + threatId

        const response = await fetch(apiUrl)
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        const data: { results: ThreatDataResult[] } = await response.json()

        // Assuming data structure { results: [...] }
        if (data.results && data.results.length > 0) {
          setThreatData(data.results[0])
        } else {
          setError('Threat data not found.')
        }
      } catch (e) {
        console.error('Fetch error:', e)
        setError('Failed to load threat details.')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [threatId])

  // --- Loading and Error States ---
  if (loading) return <div>Loading threat details. Please wait...</div>
  if (error) return <div>Error: {error}</div>
  if (!threatData) return <div>No threat data available.</div>

  const {
    threatName,
    dateLastModified,
    familyDescription,
    description,
    engine,
    product,
    detection,
    mitre_tags,
  } = threatData

  // If the engine value exists in the map (taken from threatlabz-client), use the renamed value; otherwise, use the original value.
  const displayEngine = engineRename[engine] || engine

  const detectionDetails = parseDetectionData(detection)

  const formattedMitre = formatMitreData(mitre_tags)

  return (
    <>
      <LandingPageContainer className={`${styles.pageWrapper}`}>
        <Head>
          <title>{threatName} - Threat Detail</title>
        </Head>

        <div className={`detailTopNav pb-5 md:pb-[30px] font-gt-haptik`}>
          {/* Return to Search Results Link (Preserves query strings) */}
          <button
            onClick={handleReturnToSearch}
            className={`btnReturnToResults flex font-gt-haptik text-[16px] font-medium`}
          >
            <Image
              src={ShortBackArrowWhite.src}
              alt="Previous Result Page"
              width={16}
              height={16}
              className={`inline-block! align-middle backArrow mr-2.5`}
            />
            Return to search results
          </button>
        </div>

        <header className={`${styles.threatHeader} flex mt-[30px] flex-col md:flex-row`}>
          <h1 className="font-medium text-white leading-[110%] text-[22px] flex">
            <Image
              src={WarningRedTriangle.src}
              alt="Caution Icon"
              width={24}
              height={24}
              className={`inline-block! align-middle mr-2.5`}
            />
            {threatName}
          </h1>
          <p
            className={`${styles.lastUpdated} text-[16px] md:text-[12px] font-medium tracking-[2.4px] leading-[110%] text-[#66748f]`}
          >
            LAST UPDATED:{' '}
            {new Date(dateLastModified)
              .toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
              .toUpperCase()}
          </p>
        </header>

        {familyDescription && (
          <p
            className={`${styles.description} text-[16px] md:text-[14px] mt-2.5 text-white opacity-70`}
          >
            {familyDescription}
          </p>
        )}
        {description && (
          <p
            className={`${styles.description} text-[16px] md:text-[14px] mt-2.5 text-white opacity-70`}
          >
            {description}
          </p>
        )}
        <div className={`${styles.tagsContainer} flex mt-5`}>
          <span className={styles.tag}>ENGINE: {displayEngine}</span>
          <span className={styles.tag}>PRODUCT: {product}</span>
        </div>

        {/* Detection Details Section */}
        <section className={`${styles.detailSection}`}>
          <h2
            className={`${styles.detailHeading} text-[20px] font-medium font-gt-haptik mb-[30px]`}
          >
            Detection details
          </h2>
          {/* Check if there are actual detection details to display */}
          {detectionDetails.length > 0 ? (
            <div className={styles.detailTableWrapper}>
              <table className={styles.detailTable}>
                <thead>
                  <tr className={styles.rowLabel}>
                    <th>Connection</th>
                    <th>Category</th>
                    <th>Type</th>
                    <th>Detection ID</th>
                    <th>Severity</th>
                    <th>Connection Port</th>
                  </tr>
                </thead>
                <tbody>
                  {detectionDetails.map((detail, index) => (
                    <tr key={index} className={styles.rowData}>
                      <td>{detail.connection}</td>
                      <td>{detail.category}</td>
                      <td>{detail.type}</td>
                      <td>{detail.detectionId}</td>
                      <td>{detail.severity}</td>
                      <td>{detail.connectionPort}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No detection details available for this threat.</p>
          )}
        </section>

        {/* Mitre Att&ck Matrix Section (Using TileSwiper) */}
        {formattedMitre.length > 0 && (
          <section className={styles.mitreMatrixSection}>
            <h2 className="text-[20px] font-medium font-gt-haptik mb-5">Mitre Att&ck Matrix</h2>
            <div className={styles.mitreMatrixVersion}>
              <span className={`${styles.mitreMatrixVersionLabel} inline-flex py-2.5 px-[50px]`}>
                V1.X
              </span>
            </div>

            {/* Use TileSwiper for the formatted MITRE data */}

            <div className={styles.mitreMatrixPersistenceBox}>
              <TileSwiper
                data={formattedMitre}
                variant="mitreAttack"
                externalStyles={{
                  mitreMatrixCard: styles.mitreMatrixCard,
                  tactic: styles.tactic,
                  attackItem: styles.attackItem,
                }}
                customBreakpoints={{
                  // Keep small screen defaults
                  0: {
                    slidesPerView: 'auto' as const,
                    slidesPerGroup: 1,
                    freeMode: { enabled: true, sticky: true },
                  },
                  768: {
                    slidesPerView: 'auto' as const,
                    slidesPerGroup: 1,
                    freeMode: { enabled: true, sticky: true },
                    pagination: false,
                  },
                  // Override 1024+ to show 3 columns instead of 4
                  1024: {
                    slidesPerView: 'auto' as const,
                    slidesPerGroup: 3, // Changed from 4 to 3
                    pagination: false,
                    navigation: false,
                    allowTouchMove: false,
                    freeMode: false,
                  },
                }}
              />
            </div>
          </section>
        )}
      </LandingPageContainer>
      <Footer />
    </>
  )
}
